import cloneDeep from 'lodash/cloneDeep';
import {
    YEAR_GROUP_LIST,
    TEACHER_LIST,
    LIST_TO_ALL_OPTION,
    CLASS_LIST,
    GROUP_LIST,
    STUDENT_LIST,
    REPORT_LIST,
    ALL_GROUPS_OPTION,
    ALL_STUDENTS_OPTION,
    FILTER_TO_DROPDOWN_MAP,
    ALL_GROUPS,
    ALL_CLASSES,
    ALL_STUDENTS,
    ALL_TEACHERS_OPTION,
    ALL_CLASSES_OPTION,
    DATE_LIST,
    SELECTED_YEAR_GROUP,
    ALL_YEARS,
    SELECTED_TEACHER,
    ALL_TEACHERS,
    SELECTED_CLASS,
    SELECTED_GROUP
} from '../../filter-constants';
import studentsByGroupActions from '../students-by-group/students-by-group-actions';
import yearGroupsSelectors from '../year-groups/year-groups-selectors';
import dropdownDataSelectors from './dropdown-data-selectors';
import activeFiltersSelectors from '../active-filters/active-filters-selectors';
import studentsByGroupSelectors from '../students-by-group/students-by-group-selectors';

export const SET_DROPDOWN_DATA = 'SET_DROPDOWN_DATA';
export const UPDATE_DROPDOWN_DATA_ITEM = 'UPDATE_DROPDOWN_DATA_ITEM';

/**
 * Updates the dropdown data in the state for each list passed in
 * Merges data with the current state
 *
 * @param {object} lists - should have list type as key, with value of an object containing properties that need
 * to be overridden in the dropdown data (data, selectedOption, disabled), any properties not provided will keep
 * their current value
 *  {
 *      [CLASS_LIST]: { data: [...], selectedOption: {...} },
 *      [GROUP_LIST]: { selectedOption: {...} }
 *  }
 */
function setDropdownData(lists) {
    return (dispatch, getState) => {
        const { dropdownData: prevDropdownData } = getState();
        const dropdownData = cloneDeep(prevDropdownData);

        const listTypes = [
            YEAR_GROUP_LIST,
            TEACHER_LIST,
            CLASS_LIST,
            GROUP_LIST,
            STUDENT_LIST,
            REPORT_LIST,
            DATE_LIST
        ];

        listTypes.forEach((listType) => {
            const listItem = lists[listType];
            if (listItem) {
                const dropdown = dropdownData.find(
                    (dd) => dd.type === listType
                );
                if (dropdown) {
                    let data;
                    const selectedOption =
                        listItem.selectedOption || LIST_TO_ALL_OPTION[listType];

                    let disabled = listItem.disabled;

                    // if only one item in list we set as selected option and disable list
                    // if more than one item we add in the all option as first in list
                    if (listItem.data) {
                        const allOption = LIST_TO_ALL_OPTION[listType];
                        if (listItem.data.length > 1) {
                            if (allOption) {
                                if (
                                    listItem.data[0].value === allOption.value
                                ) {
                                    data = listItem.data;
                                } else {
                                    data = [allOption, ...listItem.data];
                                }
                            }
                            disabled = false;
                        } else {
                            data = listItem.data;
                            disabled = true;

                            if (
                                allOption &&
                                listItem.data[0] &&
                                listItem.data[0].value !== allOption.value
                            ) {
                                data = [allOption, ...listItem.data];
                                disabled = false;
                            }
                        }
                    }

                    dropdown.data = data || dropdown.data;
                    dropdown.selectedOption =
                        selectedOption || dropdown.selectedOption;
                    dropdown.disabled =
                        disabled != null ? disabled : dropdown.disabled;
                }
            }
        });

        dispatch({
            type: SET_DROPDOWN_DATA,
            payload: dropdownData
        });
    };
}

/**
 * Sets year group and teacher data in dropdowns
 */
function setYearGroups(yearGroupData) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedYearId = activeFiltersSelectors.selectYearGroupId(state);
        const selectedTeacherId = activeFiltersSelectors.selectTeacherId(state);
        dispatch(
            setDropdownData({
                [YEAR_GROUP_LIST]: {
                    data: yearGroupData.map((y) => ({
                        value: y.id,
                        label: y.year
                    }))
                },
                [TEACHER_LIST]: {
                    data: yearGroupsSelectors.selectTeachersByYear(
                        selectedYearId
                    )(state)
                },
                [CLASS_LIST]: {
                    data: yearGroupsSelectors.selectClassesByYearAndTeacher(
                        selectedYearId,
                        selectedTeacherId
                    )(state)
                }
            })
        );
    };
}

/**
 * Sets properties of the class dropdown list using the passed in class list
 * Handles auto selecting class and disabling if teacher has only 1 class
 */
function setClassroomList(classData) {
    return (dispatch) => {
        dispatch(
            setDropdownData({
                [CLASS_LIST]: {
                    data: classData.map((classRoom) => ({
                        label: classRoom.name,
                        value: classRoom.id,
                        isClassEmpty: classRoom.isClassEmpty
                    }))
                }
            })
        );
    };
}

/**
 * Sets properties of group and student dropdown lists using group
 * data from backend
 */
function setGroupAndStudentLists(groups) {
    return (dispatch) => {
        dispatch(
            setDropdownData({
                [GROUP_LIST]: {
                    data: groups.map((group) => ({
                        label: group.name,
                        value: group.id
                    }))
                },
                [STUDENT_LIST]: {
                    data: groups.length > 0 ? groups[0].students : []
                }
            })
        );
    };
}

/**
 * Sets the selected options of the drop down menus from the active filters
 */
function setSelectedOptionsFromFilters(activeFilters) {
    return (dispatch, getState) => {
        const state = getState();

        const lists = {};
        for (const key in activeFilters) {
            const dropdownType = FILTER_TO_DROPDOWN_MAP[key];
            if (dropdownType) {
                const dropdownItem = {};

                const data = getDropdownDataFromFilters(
                    dropdownType,
                    activeFilters,
                    state
                );
                if (data) {
                    dropdownItem.data = data;
                }

                if (dropdownType === STUDENT_LIST) {
                    const selectedClassId = activeFilters[SELECTED_CLASS].id;
                    const selectedGroupId = activeFilters[SELECTED_GROUP].id;
                    const students = studentsByGroupSelectors.selectStudentsForGroup(
                        selectedGroupId
                    )(state);
                    if (
                        students &&
                        selectedGroupId !== ALL_GROUPS &&
                        selectedClassId !== ALL_CLASSES
                    ) {
                        dropdownItem.data = students;
                    }
                }

                if (dropdownType === DATE_LIST) {
                    dropdownItem.selectedOption = activeFilters[key];
                } else {
                    dropdownItem.selectedOption = {
                        value: activeFilters[key].id,
                        label: activeFilters[key].name
                    };
                }

                lists[dropdownType] = dropdownItem;
            }
        }

        dispatch(setDropdownData(lists));
    };
}

function getDropdownDataFromFilters(dropdownType, activeFilters, state) {
    const selectedYearGroup =
        activeFilters[SELECTED_YEAR_GROUP].id !== null
            ? activeFilters[SELECTED_YEAR_GROUP].id
            : ALL_YEARS;
    const selectedTeacher = activeFilters[SELECTED_TEACHER].id || ALL_TEACHERS;
    const selectedGroup = activeFilters[SELECTED_GROUP];

    switch (dropdownType) {
        case TEACHER_LIST: {
            return yearGroupsSelectors.selectTeachersByYear(selectedYearGroup)(
                state
            );
        }
        case CLASS_LIST: {
            return yearGroupsSelectors.selectClassesByYearAndTeacher(
                selectedYearGroup,
                selectedTeacher
            )(state);
        }
        case STUDENT_LIST: {
            if (selectedGroup.id !== ALL_GROUPS) {
                return studentsByGroupSelectors.selectStudentsForGroup(
                    selectedGroup
                )(state);
            }
            return dropdownDataSelectors.selectDropdownDataDatalistByType(
                dropdownType
            )(state);
        }
        default:
            return dropdownDataSelectors.selectDropdownDataDatalistByType(
                dropdownType
            )(state);
    }
}

/**
 * Sets year group as selected option in dropdown data
 * Filters both teacher and class lists to only ones within the selected year
 * If selected teacher has classes within that year, keeps selected else selects All teachers
 * If selected class is within that year, keeps selected else selects All classes
 */
function setSelectedYearGroup(option) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedYearGroup = dropdownDataSelectors.selectSelectedOptionByType(
            YEAR_GROUP_LIST
        )(state);

        if (selectedYearGroup.value !== option.value) {
            const yearId = option.value;

            // If user has previously selected a teacher in dropdown, keep selection if teacher has
            // classes within the year group, else select All teachers
            const teachers = yearGroupsSelectors.selectTeachersByYear(yearId)(
                state
            );
            let selectedTeacher = ALL_TEACHERS_OPTION;
            const ddTeacher = dropdownDataSelectors.selectSelectedOptionByType(
                TEACHER_LIST
            )(state);
            if (teachers.find((t) => t.value === ddTeacher.value)) {
                selectedTeacher = ddTeacher;
            }

            const classes = yearGroupsSelectors.selectClassesByYearAndTeacher(
                yearId,
                selectedTeacher.value
            )(state);

            setDropdownDataForYearGroupTeacher(dispatch, state, classes, {
                [YEAR_GROUP_LIST]: { selectedOption: option },
                [TEACHER_LIST]: {
                    data: teachers,
                    selectedOption: selectedTeacher
                }
            });
        }
    };
}

/**
 * Sets teacher as selected option
 * Filters class list to only those classes that belong to the teacher, keeps
 * the year filter applied if user has selected a school year
 */
function setSelectedTeacher(option) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedTeacher = dropdownDataSelectors.selectSelectedOptionByType(
            TEACHER_LIST
        )(state);

        if (selectedTeacher.value !== option.value) {
            const selectedYear = dropdownDataSelectors.selectSelectedOptionByType(
                YEAR_GROUP_LIST
            )(state);
            const classes = yearGroupsSelectors.selectClassesByYearAndTeacher(
                selectedYear.value,
                option.value
            )(state);

            setDropdownDataForYearGroupTeacher(dispatch, state, classes, {
                [TEACHER_LIST]: {
                    selectedOption: option
                }
            });
        }
    };
}

/**
 * Handles the shared logic between setting selected year group + teacher
 * Dispatches action to set dropdown data
 * Either retrieves group + student data if there is only one class in year/teacher combo, or clears
 * current group + student data in redux store
 */
function setDropdownDataForYearGroupTeacher(
    dispatch,
    state,
    classes,
    ddOptions
) {
    // If user has previously selected a class in dropdown, keep selection if class is
    // for the selected year/teacher combo, else select All classes
    const ddClass = dropdownDataSelectors.selectSelectedOptionByType(
        CLASS_LIST
    )(state);
    let selectedClass = ALL_CLASSES_OPTION;
    let groupStudentOptions = {
        [GROUP_LIST]: { data: [ALL_GROUPS_OPTION] },
        [STUDENT_LIST]: { data: [ALL_STUDENTS_OPTION] }
    };
    if (classes.find((c) => c.value === ddClass.value)) {
        selectedClass = ddClass;
        groupStudentOptions = {};
    }

    dispatch(
        setDropdownData({
            [CLASS_LIST]: {
                data: classes,
                selectedOption: selectedClass
            },
            ...groupStudentOptions,
            ...ddOptions
        })
    );

    if (classes.length === 1) {
        dispatch(studentsByGroupActions.getStudentsByGroup(classes[0].value));
    } else {
        dispatch(studentsByGroupActions.resetStudentsByGroup());
    }
}

/**
 * Sets class as selected in dropdown data
 * If user selects All Classes, updates group & student list data and selected option
 * If user selects a particular class, loads the groups & students for the class
 */
function setSelectedClass(option) {
    return (dispatch, getState) => {
        const selectedClass = dropdownDataSelectors.selectSelectedOptionByType(
            CLASS_LIST
        )(getState());

        if (selectedClass.value !== option.value) {
            if (option.value !== ALL_CLASSES) {
                dispatch(
                    studentsByGroupActions.getStudentsByGroup(option.value)
                );
            }
            dispatch(
                setDropdownData({
                    [CLASS_LIST]: { selectedOption: option },
                    [GROUP_LIST]: { data: [ALL_GROUPS_OPTION] },
                    [STUDENT_LIST]: { data: [ALL_STUDENTS_OPTION] }
                })
            );
        }
    };
}

/**
 * Sets group as selected in dropdown data
 * If user selects All groups, updates student list data and selected option
 * If user selects particular group, filters student list to only those in the group
 */
function setSelectedGroup(option) {
    return (dispatch, getState) => {
        const state = getState();
        const selectedGroup = dropdownDataSelectors.selectSelectedOptionByType(
            GROUP_LIST
        )(state);

        if (selectedGroup.value !== option.value) {
            const updatedDropdownData = {
                [GROUP_LIST]: { selectedOption: option }
            };

            const group = studentsByGroupSelectors.selectGroupById(
                option.value
            )(state);
            if (group) {
                updatedDropdownData[STUDENT_LIST] = {
                    data: group.students,
                    selectedOption: ALL_STUDENTS_OPTION
                };
            }

            dispatch(setDropdownData(updatedDropdownData));
        }
    };
}

/**
 * Sets student as selected in dropdown data
 * If user selects particular student, updates selected group to that student's group
 */
function setSelectedStudent(option) {
    return (dispatch, getState) => {
        let dropdownData;
        const state = getState();
        const selectedStudent = dropdownDataSelectors.selectSelectedOptionByType(
            STUDENT_LIST
        )(state);

        if (selectedStudent.value !== option.value) {
            if (option.value === ALL_STUDENTS) {
                dropdownData = {
                    [STUDENT_LIST]: { selectedOption: option }
                };
            } else {
                const studentGroup = studentsByGroupSelectors.selectGroupForStudent(
                    option.value
                )(getState());
                dropdownData = {
                    [GROUP_LIST]: {
                        selectedOption: {
                            label: studentGroup.name,
                            value: studentGroup.id
                        }
                    },
                    [STUDENT_LIST]: { selectedOption: option }
                };
            }

            dispatch(setDropdownData(dropdownData));
        }
    };
}

function setSelectedDate(option) {
    return (dispatch, getState) => {
        const dateOption = dropdownDataSelectors.selectDropdownDataByType(
            DATE_LIST
        )(getState());
        dispatch({
            type: UPDATE_DROPDOWN_DATA_ITEM,
            payload: {
                type: DATE_LIST,
                item: {
                    ...dateOption,
                    selectedOption: option
                }
            }
        });
    };
}

export default {
    setDropdownData,
    setYearGroups,
    setClassroomList,
    setGroupAndStudentLists,
    setSelectedOptionsFromFilters,
    setSelectedYearGroup,
    setSelectedTeacher,
    setSelectedClass,
    setSelectedGroup,
    setSelectedStudent,
    setSelectedDate
};



// WEBPACK FOOTER //
// ./components/report-filters/redux/dropdown-data/dropdown-data-actions.js